import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { Checkbox } from 'antd';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setRestaurantsForPush: (value: string[]) => void;
	selectedAdditionalRestaurants: string[];
	isVisibleAdditionalModal: boolean;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	setRestaurantsForPush,
	selectedAdditionalRestaurants,
	isVisibleAdditionalModal,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [isSelectedAllRestaurants, setIsSelectedAllRestaurants] = useState<boolean>(false);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const visibleData = useMemo(() => {
		const items = showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || selectedAdditionalRestaurants.includes(el.id));

		return items.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [data, showArchived, selectedAdditionalRestaurants]);

	const filteredRestaurants = useMemo(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const handleSelectAllRestaurants = () => {
		if (isSelectedAllRestaurants) {
			setRestaurantsForPush([]);
		} else {
			const IDsOfAllRestaurants = visibleData.map((el) => el.id);
			setRestaurantsForPush(IDsOfAllRestaurants);
		}
	};

	useEffect(() => {
		setSearchString('');
	}, [isVisibleAdditionalModal]);

	useEffect(() => {
		if (selectedAdditionalRestaurants.length === 0) {
			setIsSelectedAllRestaurants(false);
		} else if (selectedAdditionalRestaurants.length === visibleData.length) {
			setIsSelectedAllRestaurants(true);
		} else if (selectedAdditionalRestaurants.length !== visibleData.length) {
			setIsSelectedAllRestaurants(false);
		}
	}, [selectedAdditionalRestaurants.length, visibleData.length]);

	const renderSelectMenu = (menu: React.ReactElement) => {
		return (
			<>
				<div className={style.selectMenuHeader}>
					<Checkbox
						className={style.checkBox}
						checked={isSelectedAllRestaurants}
						disabled={!visibleData.length}
						onChange={handleSelectAllRestaurants}>
						<Text translationNameSpace="form" translationKey="selectAllPlaceholder" asFragment={true} />
					</Checkbox>
					<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
						<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
					</Checkbox>
					<SearchField
						onChangeSelectSearchString={setSearchString}
						searchString={searchString}
						className={style.searchField}
					/>
				</div>
				{menu}
			</>
		);
	};

	return (
		<OutsideFormSelect
			testId="MultipleSelect"
			name="IDsSelectedRestaurantsForChangeDishPosition"
			label="restaurantsLabel"
			value={selectedAdditionalRestaurants}
			options={filteredRestaurants}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={isLoading}
			onChange={setRestaurantsForPush}
			counterValue={selectedAdditionalRestaurants.length}
			withValidation={false}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
